import React, { useEffect, useState } from "react";
import { Navigate, useLocation, Link } from "react-router-dom";
import AuthService from "../utils/AuthService";

/**
 * ✅ FINAL FIXED ProtectedRoute
 * - Works with admin_token / dept_token / viewer_token / venue_admin_token
 * - Evaluates Dynamic Permissions (Feature Flags)
 */
export default function ProtectedRoute({ requiredRole, requiredFeature, children }) {
  const location = useLocation();
  const [session, setSession] = useState(() => AuthService.autoLogin());
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const recheck = () => {
      const current = AuthService.autoLogin();
      setSession(current);
      setChecking(false);
    };

    recheck();

    // Listen for logout across tabs
    window.addEventListener("logout", recheck);

    return () => {
      window.removeEventListener("logout", recheck);
    };
  }, [location.pathname]);

  // ⏳ Wait a bit while verifying session
  if (checking) return null;

  // 🚫 No active session — redirect to login
  if (!session?.token) {
    return <Navigate to="/" replace />;
  }

  const { role, user } = session;
  const normalizedRequired = requiredRole?.toUpperCase();

  // ✅ Check role access logic
  // Allow BOTH ADMIN and VENUE_ADMIN into the "ADMIN" required portal
  if (normalizedRequired === "ADMIN" && role !== "ADMIN" && role !== "VENUE_ADMIN") {
    if (role === "VIEWER") return <Navigate to="/viewer/register" replace />;
    return <Navigate to="/dept" replace />;
  }

  if (normalizedRequired === "DEPARTMENT" && role !== "DEPARTMENT") {
    if (role === "ADMIN" || role === "VENUE_ADMIN") return <Navigate to="/admin" replace />;
    if (role === "VIEWER") return <Navigate to="/viewer/register" replace />;
  }

  if (normalizedRequired === "VIEWER" && role !== "VIEWER") {
    if (role === "ADMIN" || role === "VENUE_ADMIN") return <Navigate to="/admin" replace />;
    if (role === "DEPARTMENT") return <Navigate to="/dept" replace />;
  }

  // ✅ Check Dynamic Feature Flags for VENUE_ADMIN
  if (role === "VENUE_ADMIN" && requiredFeature) {
    const perms = user?.permissions || {};
    
    // If the permission is 0 or undefined, block access and show a clean UI
    if (perms[requiredFeature] !== 1) {
      return (
        <div className="flex flex-col items-center justify-center min-h-[60vh] bg-transparent p-6 text-center">
          <h1 className="text-8xl font-black text-red-500/10 absolute">403</h1>
          <div className="relative z-10 flex flex-col items-center">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-800 mb-2">Access Denied</h2>
            <p className="text-slate-500 mb-8 max-w-md">
              Your Venue Admin account does not have permission to view this specific module. 
              Please contact the primary HR Administrator if you require access.
            </p>
            <Link 
              to="/admin/requests" 
              className="px-6 py-2 bg-blue-600 text-white rounded-xl font-bold shadow-md hover:bg-blue-700 transition"
            >
              Back to Requests
            </Link>
          </div>
        </div>
      );
    }
  }

  // ✅ Session valid and role allowed
  return children;
}